import React, { useState } from "react";
import { useAuth } from "../providers/AuthContext";

interface ChangePasswordModalProps {
  isForced: boolean;
  onClose?: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isForced,
  onClose,
}) => {
  const { changePassword, loading, error, clearError } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();

    if (newPassword !== confirmPassword) {
      setLocalError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setLocalError("Password must be at least 8 characters");
      return;
    }

    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
      if (!isForced && onClose) {
        setTimeout(onClose, 1500);
      }
    } catch {
      // Error is handled by AuthContext
    }
  };

  const handleClose = () => {
    if (!isForced && onClose) {
      onClose();
    }
  };

  const displayError = localError || error;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
      onClick={isForced ? undefined : handleClose}
    >
      <div
        className="card"
        style={{ maxWidth: "400px", width: "90%", margin: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginBottom: "1rem" }}>
          {isForced ? "Password Change Required" : "Change Password"}
        </h2>

        {isForced && (
          <p style={{ marginBottom: "1rem", color: "var(--color-text-secondary)" }}>
            You must change your password before continuing.
          </p>
        )}

        {displayError && (
          <div className="error" style={{ marginBottom: "1rem" }}>
            {displayError}
          </div>
        )}

        {success ? (
          <div
            style={{
              padding: "0.75rem",
              backgroundColor: "rgba(34, 139, 34, 0.1)",
              border: "1px solid var(--color-success)",
              borderRadius: "var(--radius-md)",
              color: "var(--color-success)",
              textAlign: "center",
            }}
          >
            Password changed successfully!
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input
                type="password"
                className="form-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ flex: 1 }}
                disabled={loading}
              >
                {loading ? "..." : "Change Password"}
              </button>
              {!isForced && onClose && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
